<template>
  <div class="w-full">
    <a-select
      ref="selectRef"
      v-model:value="selectedLists"
      class="w-full"
      mode="multiple"
      placeholder="Select list(s)"
      :open="dropdownOpen"
      :options="listOptions"
      @dropdown-visible-change="handleDropdownVisibleChange"
      :get-popup-container="(triggerNode) => triggerNode.parentNode"
    >
      <template #suffixIcon>
        <ThemeImage
          class="size-4"
          :icon="imageNames.BOTTOM_ARROW"
          alt="bottom_white_arrow"
        />
      </template>
      <template #dropdownRender="{ menuNode: menu }">
        <div class="p-1">
          <div class="flex flex-col">
            <div class="flex flex-col gap-2">
              <div class="flex flex-row gap-1">
                <div
                  v-for="tab in tabs"
                  :key="tab.id"
                  :class="['list_category px-2 py-1 font-semibold rounded-lg cursor-pointer text-sm transition-all duration-300 ease-in-out', { active: activeTab === tab.id }]"
                  @click="handleTabClick(tab.id)"
                >
                  {{ tab.name }}
                </div>
              </div>

              <CustomInput
                ref="searchInputRef"
                format="text"
                v-model="searchQuery"
                placeholder="Search here"
                type="primary"
                :icon="imageNames.SEARCH_VARIANT1"
                size="size-4"
                alt="search_icon"
              />

              <div class="separator"></div>
            </div>

            <div class="h-[190px] overflow-y-auto">
              <div v-if="loading" class="flex justify-center items-center h-full">
                <a-spin />
              </div>
              <EmptyScreen
                v-else-if="displayLists.length === 0"
                :source="activeTab"
                text="No list(s) found!"
              />
              <div v-else class="py-1 flex flex-col">
                <div
                  v-for="list in displayLists"
                  :key="list.id"
                  :class="['dropdown_item !items-start !flex-col !px-2 !py-1 !mb-[2px] !gap-0', { 'selected': selectedLists.includes(list.id) }]"
                  @click="toggleListSelection(list.id)"
                >
                  <div class="text-sm">{{ list.list_name || list.name }}</div>
                  <div class="text-xs secondary_color">{{ listTypeTitles[list.list_type] || 'Contact' }} list</div>
                </div>
              </div>
            </div>
            <div class="separator"></div>
            <div class="mt-2">
              <button
                class="w-full py-1 flex items-center gap-2 text-sm rounded-lg list_category"
                @click="handleCreateList"
              >
                <div class="secondary_color">
                  <ThemeImage :icon="imageNames.PLUS" alt="plus_icon" class="size-6 opacity-40" />
                </div>
                <span class="text-sm text_color">Create New List</span>
              </button>
            </div>
          </div>
        </div>
      </template>
    </a-select>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, defineExpose, onUnmounted } from "vue";
import CustomInput from "@/src/components/CustomInput.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

import { toGetAllEntityLists } from "@/src/utils/utils.js";
import eventBus from "@/src/utils/eventBus.js";
import DashboardService from "@/src/api/DashboardService.js";
import {imageNames} from "@/src/constants/imageConstants.js";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  }
});

const emit = defineEmits(['update:modelValue', 'create-list']);

const isOpen = ref(false);
const searchQuery = ref('');
const activeTab = ref('all');
const searchInputRef = ref(null);
const loading = ref(false);
const allLists = ref([]);
const currentUser = ref(null);
const selectRef = ref(null);
const dropdownOpen = ref(false);

const tabs = [
  { id: 'all', name: 'All Lists' },
  { id: 'my', name: 'My Lists' }
];

const listTypeTitles = {
  contact: 'Contact',
  lead: 'Lead',
  company: 'Company'
};

const selectedLists = computed({
  get: () => props.modelValue || [],
  set: (value) => emit('update:modelValue', value)
});

const myLists = computed(() => {
  if (!currentUser.value?.id) return [];
  const currentUserId = Number(currentUser.value.id);
  return allLists.value.filter(list =>
    list.created_by && Number(list.created_by) === currentUserId
  );
});

const displayLists = computed(() => {
  const lists = activeTab.value === 'my' ? myLists.value : allLists.value;
  if (!searchQuery.value) return lists;

  const query = searchQuery.value.toLowerCase();
  return lists.filter(list =>
    (list.list_name?.toLowerCase() || list.name?.toLowerCase() || '').includes(query)
  );
});

const listOptions = computed(() => {
  return allLists.value.map(list => ({
    label: list.list_name || list.name,
    value: list.id
  }));
});

const toggleListSelection = (listId) => {
  const index = selectedLists.value.indexOf(listId);
  const newSelection = [...selectedLists.value];

  if (index === -1) {
    newSelection.push(listId);
  } else {
    newSelection.splice(index, 1);
  }

  selectedLists.value = newSelection;
};

const handleDropdownVisibleChange = (visible) => {
  dropdownOpen.value = visible;
  if (visible) {
    fetchLists();
    nextTick(() => searchInputRef.value?.focus());
  } else {
    searchQuery.value = '';
  }
  isOpen.value = visible;
};

const fetchLists = async () => {
  loading.value = true;
  try {
    const [contactLists, leadLists, companyLists] = await Promise.all([
      toGetAllEntityLists('contact'),
      toGetAllEntityLists('lead'),
      toGetAllEntityLists('company')
    ]);

    allLists.value = [
      ...(contactLists?.map(list => ({ ...list, list_type: 'contact' })) || []),
      ...(leadLists?.map(list => ({ ...list, list_type: 'lead' })) || []),
      ...(companyLists?.map(list => ({ ...list, list_type: 'company' })) || [])
    ];
  } catch (error) {
    console.error('Error fetching lists:', error);
  } finally {
    loading.value = false;
  }
};

const fetchCurrentUser = async () => {
  try {
    const response = await DashboardService.fetchUsers();
    if (response?.success && response.users?.length > 0) {
      currentUser.value = response.users[0];
    }
  } catch (error) {
    console.error('Error fetching user profile:', error);
  }
};

const handleCreateList = () => {
  dropdownOpen.value = false;
  setTimeout(() => emit('create-list'), 200);
};

const handleTabClick = (tabId) => {
  activeTab.value = tabId;
  searchQuery.value = '';
};

onMounted(() => {
  fetchLists();
  fetchCurrentUser();
  eventBus.on('refresh-entity-lists', fetchLists);
});

onUnmounted(() => {
  eventBus.off('refresh-entity-lists', fetchLists);
});

defineExpose({
  focusSearch: () => nextTick(() => searchInputRef.value?.focus()),
  isOpen
});
</script>

<style scoped>
.list_category {
  color: var(--secondary-color);
  line-height: 17px;
  white-space: nowrap;
}

.list_category:hover {
  background-color: var(--white-opacity-6);
}

.list_category.active {
  background-color: var(--white-opacity-6);
  color: var(--text-color);
}

.separator {
  height: 1px;
  background-color: var(--white-opacity-8);
  margin: 0 -8px;
  width: calc(100% + 16px);
}
</style>